/**
 * Sort the keys of an object recursively
 * @param obj
 * @returns Sorted object
 */
export function sortKeys<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(sortKeys) as unknown as T;
  if (obj && typeof obj === 'object' && obj.constructor === Object) {
    const objRecord = obj as Record<string, unknown>;
    const sortedObj = Object.keys(obj)
      .sort((keyA, keyB) => keyA.localeCompare(keyB))
      .reduce((acc, key) => {
        acc[key] = sortKeys(objRecord[key]);
        return acc;
      }, {} as Record<string, unknown>);
    return sortedObj as T;
  }
  return obj;
}

const HEX_RADIX = 16;
const HEX_PAD_LENGTH = 2;

/** Generate a stable SHA-256 hex digest from an object's recursively sorted JSON representation. */
export async function generateObjectHash(data: object): Promise<string> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('generateObjectHash requires the Web Crypto API in a browser environment.');
  }

  const canonicalData = JSON.stringify(sortKeys(data));
  const encodedData = new TextEncoder().encode(canonicalData);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encodedData);

  return Array.from(new Uint8Array(hashBuffer))
    .map(byte => byte.toString(HEX_RADIX).padStart(HEX_PAD_LENGTH, '0'))
    .join('');
}

/**
 * Check if two objects are the same by comparing their properties.
 * Only works with objects containing primitive values or arrays of primitives.
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @returns True if objects have the same properties and values
 */
export function isSameObject(obj1: Object, obj2: Object): boolean {
  return !isDifferentObject(obj1, obj2);
}

/**
 * Check if two objects are different by comparing their properties.
 * Only works with objects containing primitive values or arrays of primitives.
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @returns True if objects have different properties or values
 */
export function isDifferentObject(obj1: Object, obj2: Object): boolean {
  return Object.keys(obj1).some(key => !isSameValue(obj1[key as keyof typeof obj1], obj2[key as keyof typeof obj2]));
}

/** Arrays compare by items: e.g. vue-router re-creates repeated query values on every navigation. */
function isSameValue(value1: unknown, value2: unknown): boolean {
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return value1.length === value2.length && value1.every((item, index) => item === value2[index]);
  }
  return value1 === value2;
}

/**
 * Check if a value is a plain object
 * @param obj Value to check
 * @returns True if the value is a plain object
 */
export function isObject(obj: unknown): boolean {
   return !!(obj && typeof obj === 'object' && obj.constructor === Object);
}

/**
 * Check if an object is empty
 * @param obj Object to check
 * @returns True if the object is empty
 */
export function isEmpty(obj: unknown | Record<string, unknown> | unknown[]): boolean {
  if (Array.isArray(obj)) {
    return obj?.length === 0;
  }
  return !!obj && isObject(obj) && Object.keys(obj)?.length === 0;
}
